import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Star, Send, MessageSquareHeart, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { api, formatApiError, pictureUrl } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { fieldCls, labelCls } from '../components/member/ProfileForm';
import { Seo, breadcrumb } from '../components/Seo';
import { useT } from '../context/LanguageContext';

const Stars = ({ value, onChange, size = 22, testId }) => (
  <div className="flex gap-1" data-testid={testId}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        disabled={!onChange}
        onClick={() => onChange?.(n)}
        data-testid={onChange ? `rating-star-${n}` : undefined}
        aria-label={`${n} stjerner`}
        className={`${onChange ? 'hover:scale-110 transition-transform' : 'cursor-default'}`}
      >
        <Star size={size} className={n <= value ? 'text-[#ffb400] fill-[#ffb400]' : 'text-white/25'} />
      </button>
    ))}
  </div>
);

const ReviewCard = ({ r, onDelete }) => {
  const pic = pictureUrl(r.picture);
  return (
    <div data-testid="review-card" className="rounded-3xl bg-[#1a1424] border border-white/5 p-6 flex gap-4">
      {pic ? (
        <img src={pic} alt={r.name} className="w-12 h-12 rounded-full object-cover shrink-0" />
      ) : (
        <div className="w-12 h-12 rounded-full bg-[#ff2e2e] text-black font-bold flex items-center justify-center shrink-0">{r.name?.[0]?.toUpperCase()}</div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <p className="font-bold text-white">{r.name}</p>
          <div className="flex items-center gap-3">
            <Stars value={r.rating} size={16} />
            {onDelete && (
              <button type="button" onClick={() => onDelete(r)} data-testid={`review-delete-${r.id}`} aria-label="Slett anmelding" className="w-8 h-8 rounded-full border border-white/10 text-white/50 hover:border-red-500 hover:text-red-400 flex items-center justify-center transition-colors"><Trash2 size={14} /></button>
            )}
          </div>
        </div>
        <p className="text-white/75 leading-relaxed whitespace-pre-wrap">{r.comment}</p>
        <p className="text-white/35 text-xs mt-3">{new Date(r.created_at).toLocaleDateString('nn-NO', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
      </div>
    </div>
  );
};

const Reviews = () => {
  const { t, lang } = useT();
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], count: 0, average: 0 });
  const [form, setForm] = useState({ name: '', rating: 5, comment: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get('/reviews').then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && !form.name) setForm((f) => ({ ...f, name: user.name }));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (r) => {
    if (!window.confirm(`${t('Slette anmeldinga frå')} ${r.name}?`)) return;
    try {
      await api.delete(`/reviews/${r.id}`);
      setData((d) => {
        const reviews = d.reviews.filter((x) => x.id !== r.id);
        const avg = reviews.length ? Math.round((reviews.reduce((s, x) => s + x.rating, 0) / reviews.length) * 10) / 10 : 0;
        return { reviews, count: reviews.length, average: avg };
      });
      toast.success(t('Anmeldinga er sletta.'));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.comment.trim().length < 3) return toast.error(t('Skriv ein litt lengre kommentar.'));
    setSending(true);
    try {
      const { data: created } = await api.post('/reviews', form);
      setData((d) => {
        const reviews = [created, ...d.reviews];
        const avg = Math.round((reviews.reduce((s, r) => s + r.rating, 0) / reviews.length) * 10) / 10;
        return { reviews, count: reviews.length, average: avg };
      });
      setForm({ name: user?.name || '', rating: 5, comment: '' });
      toast.success(t('Takk for kommentaren din!'));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-black min-h-screen text-white" data-testid="reviews-page">
      <Seo title={t('Anmeldingar og kommentarar | Refuel Weekend')} description={t('Les kva deltakarar seier om Refuel Weekend og nettsida, og legg igjen di eiga vurdering.')} path="/anmeldelser" jsonLd={breadcrumb([{ name: 'Refuel Weekend', path: '/' }, { name: t('Anmeldingar'), path: '/anmeldelser' }])} />
      <Navbar />
      <main className="max-w-[1100px] mx-auto px-4 md:px-8 py-12 md:py-16">
        <Link to="/" data-testid="reviews-back-link" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 border border-white/15 text-white hover:bg-black/70 transition-colors text-sm mb-8">
          <ArrowLeft size={16} /> {t('Tilbake')}
        </Link>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-[#ff2e2e] font-bold uppercase tracking-widest text-sm mb-2">{t('Kva meiner du?')}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{t('Anmeldingar & kommentarar')}</h1>
            <p className="text-white/60 mt-3 max-w-xl">{t('Gje oss ei tilbakemelding på nettsida eller del kva du gler deg mest til på Refuel Weekend 27.')}</p>
          </div>
          <div data-testid="reviews-summary" className="rounded-3xl border border-white/10 bg-[#0f0a17] px-6 py-4 flex items-center gap-4">
            <div className="text-5xl font-bold text-[#ffb400]" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{data.count ? data.average : '–'}</div>
            <div>
              <Stars value={Math.round(data.average)} size={18} />
              <p className="text-white/50 text-sm mt-1"><span data-testid="reviews-count">{data.count}</span> {t(data.count === 1 ? 'anmelding' : 'anmeldingar')}</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <form onSubmit={submit} data-testid="review-form" className="lg:col-span-2 rounded-3xl bg-[#0f0a17] border border-white/10 p-7 space-y-4 self-start lg:sticky lg:top-24">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-[#ff2e2e]/15 text-[#ff2e2e] flex items-center justify-center"><MessageSquareHeart size={20} /></div>
              <h2 className="text-lg md:text-xl font-bold">{t('Skriv ein kommentar')}</h2>
            </div>
            <div>
              <label className={labelCls}>{t('Namn')}</label>
              <input data-testid="review-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fieldCls} placeholder={t('Namnet ditt')} required maxLength={60} />
            </div>
            <div>
              <label className={labelCls}>{t('Vurdering')}</label>
              <Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} size={30} testId="review-rating" />
            </div>
            <div>
              <label className={labelCls}>{t('Kommentar')}</label>
              <textarea data-testid="review-comment-input" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} rows={5} className={`${fieldCls} resize-none`} placeholder={t('Kva synest du om nettsida eller Refuel Weekend?')} required maxLength={1000} />
            </div>
            <button type="submit" disabled={sending} data-testid="review-submit-button" className="w-full py-3.5 rounded-full bg-[#ff2e2e] text-black font-bold uppercase tracking-wide hover:bg-[#ff4d4d] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {sending ? t('Sender…') : t('Publiser')} {!sending && <Send size={16} />}
            </button>
          </form>

          <div className="lg:col-span-3 space-y-4" data-testid="reviews-list">
            {data.reviews.length === 0 ? (
              <div data-testid="reviews-empty" className="rounded-3xl border border-dashed border-white/15 p-10 text-center text-white/50">{t('Ingen kommentarar enno – bli den første!')}</div>
            ) : (
              data.reviews.map((r) => <ReviewCard key={r.id} r={r} onDelete={user?.is_admin ? remove : undefined} />)
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Reviews;
